import React, { useState, useEffect } from "react";
import Announcement from "./Announcement";
import { API_ENDPOINTS } from "../config/constants";

const STORAGE_KEY = "dismissed_announcements";
const MAX_DAYS = 30;
const MAX_ANNOUNCEMENTS = 3;

const AnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(null); // Initialize as null to track initialization state

  // First useEffect to handle loading dismissed IDs from localStorage
  useEffect(() => {
    const storedDismissed = localStorage.getItem(STORAGE_KEY);
    let initialDismissedIds = new Set();

    if (storedDismissed) {
      try {
        const parsedIds = JSON.parse(storedDismissed);
        // Ensure we're working with an array before creating the Set
        const idsArray = Array.isArray(parsedIds) ? parsedIds : [parsedIds];
        initialDismissedIds = new Set(idsArray);
      } catch (e) {
        console.error("Error parsing dismissed announcements:", e);
        localStorage.removeItem(STORAGE_KEY); // Clear invalid data
      }
    }

    setDismissedIds(initialDismissedIds);
  }, []);

  // Second useEffect to fetch announcements only after dismissedIds is initialized
  useEffect(() => {
    if (dismissedIds !== null) {
      // Only fetch if dismissedIds is initialized
      fetchAnnouncements();
    }
  }, [dismissedIds]);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Filter announcements by date and limit
      const filteredAnnouncements = filterAnnouncements(data);
      setAnnouncements(filteredAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  const filterAnnouncements = (unfilteredAnnouncements) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(
      now.getTime() - MAX_DAYS * 24 * 60 * 60 * 1000
    );

    return unfilteredAnnouncements
      .filter((announcement) => {
        const announcementDate = new Date(
          announcement.date || announcement.createdAt
        );
        // Ensure both IDs are strings for comparison
        const announcementId = announcement._id?.toString();

        const isNotDismissed = !dismissedIds.has(announcementId);
        const isRecent = announcementDate >= thirtyDaysAgo;

        return isNotDismissed && isRecent;
      })
      .sort(
        (a, b) =>
          new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)
      )
      .slice(0, MAX_ANNOUNCEMENTS);
  };

  const handleDismiss = (_id) => {
    const idString = _id?.toString();
    const newDismissedIds = new Set([...dismissedIds, idString]);
    setDismissedIds(newDismissedIds);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...newDismissedIds]));
    setAnnouncements(
      announcements.filter((a) => a._id.toString() !== idString)
    );
  };

  return (
    <div className="fixed bottom-0 sm:bottom-4 right-0 sm:right-4 z-50 flex flex-col-reverse gap-4 p-4 sm:p-0 w-full sm:w-auto max-w-full sm:max-w-sm">
      {dismissedIds !== null &&
        announcements.map((announcement) => (
          <Announcement
            key={announcement._id}
            announcement={announcement}
            onDismiss={handleDismiss}
          />
        ))}
    </div>
  );
};

export default AnnouncementsManager;
